import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '@/components/PageHero';
import '../styles/transit.css';

const FEATURES = [
  {
    title: 'GPS 轨迹追踪',
    desc: '车载 GPS + 司机 APP 实时定位，轨迹回放、里程统计一目了然。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
        <circle cx="12" cy="10" r="2.6" />
      </svg>
    ),
  },
  {
    title: '电子围栏',
    desc: '起运、途经、目的地围栏预设，越界偏航超时自动告警。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4v16M8 4v16M12 4v16M16 4v16M20 4v16" />
        <path d="M2 9h20M2 15h20" />
      </svg>
    ),
  },
  {
    title: '温湿度监控',
    desc: '车载传感器实时采集，超阈值告警，降低运输应激死亡风险。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 4a2 2 0 0 1 4 0v9.4a4.5 4.5 0 1 1-4 0z" />
        <circle cx="12" cy="16.5" r="1.8" />
      </svg>
    ),
  },
  {
    title: '检疫调运单核验',
    desc: 'A 证 / B 证拍照上传电子核验，证号自动关联牛只清单。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M14 3v5h5" />
        <path d="m9.5 13 1.8 1.8 3.2-3.3" />
      </svg>
    ),
  },
  {
    title: '监管驾驶舱（政府端）',
    desc: '在途车辆总览、区域运输热力、异常统计，全域监管一张图。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19a8 8 0 1 1 16 0" />
        <path d="M12 19l4-4.5" />
        <circle cx="12" cy="19" r="1.5" />
      </svg>
    ),
  },
  {
    title: '运输档案存证',
    desc: '全程轨迹、证照、签收信息哈希上链，形成不可篡改档案。',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 5h18v4H3z" />
        <path d="M4 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9" />
        <path d="m9 15 2 2 4-4" />
      </svg>
    ),
  },
];

const ROLES = [
  {
    tag: '政府端',
    title: '检疫合规 · 调运监管',
    desc: '面向农业农村局、动物卫生监督所，把活畜调运监管从“事后抽查”变为“全程可视”。',
    features: [
      '检疫合格证明在线核验，调运合规全程留痕',
      '区域在途车辆热力与异常告警一张图总览',
      '运输档案一键检索，审计与追溯有据可依',
      '跨部门数据协同，支撑监管决策与执法',
    ],
    accent: 'gov',
  },
  {
    tag: '企业端',
    title: '运输执行 · 风险控制',
    desc: '面向养殖、贸易、物流企业，让运输过程可控、成本可算、风险可防。',
    features: [
      '运输委托一键发起，车辆司机资质自动核验',
      '在途轨迹、电子围栏、温湿度实时掌握',
      '越界、偏航、超时等多渠道告警及时处置',
      '运输成本与司机绩效看板辅助经营决策',
    ],
    accent: 'biz',
  },
];

export default function Transit() {
  useEffect(() => {
    document.title = '牛只在途监管平台 | 云牛集·山东牛产业智慧平台';
  }, []);

  return (
    <>
      <PageHero
        title="牛只在途监管平台"
        desc="活牛运输全程可视，政府监管 + 企业双端协同，让每一趟运输都合规、可控、可溯。"
      />

      <section className="section">
        <div className="container">
          <div className="section-head">
            <h2>核心功能</h2>
            <p className="section-desc">从起运到签收，覆盖活牛运输全过程的六大核心能力</p>
          </div>
          <div className="transit-feat-grid">
            {FEATURES.map((item) => (
              <article key={item.title} className="transit-feat-card">
                <div className="transit-feat-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section alt">
        <div className="container">
          <div className="section-head">
            <h2>政府监管 + 企业运营 · 双端协同</h2>
            <p className="section-desc">同一套数据底座，服务两类角色：政府管合规，企业管执行</p>
          </div>
          <div className="transit-role-grid">
            {ROLES.map((item) => (
              <article key={item.tag} className={`transit-role-card ${item.accent}`}>
                <span className="transit-role-tag">{item.tag}</span>
                <h3>{item.title}</h3>
                <p className="transit-role-desc">{item.desc}</p>
                <ul className="transit-role-features">
                  {item.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="transit-cta">
        <div className="container transit-cta-inner">
          <h2>让每一趟活牛运输都全程可监管</h2>
          <p>咨询云牛集团队，了解在途监管平台如何接入您的业务</p>
          <div className="transit-cta-actions">
            <a href="https://transport.yunniuji.com" target="_blank" rel="noreferrer" className="btn btn-primary">
              进入在途监管平台
            </a>
            <Link to="/contact" className="btn btn-outline-light">
              免费咨询
            </Link>
            <Link to="/solutions" className="btn btn-outline-light">
              了解解决方案
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
